/**
 * @lines98/client/lib/mode-preview — open a non-daily mode in lazy-on-preview
 * (run-persistence-lifecycle.md T-A / addendum-001 Решение 1).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: owner отверг «подмену seed» — раньше превью было на
 *   КЛИЕНТСКОМ seed, а «Начать» дёргал `POST /game/start` и ПЕРЕКЛАДЫВАЛ доску.
 *   Теперь серверная сессия выдаётся на ВХОД в превью (lazy-on-preview): превью
 *   рендерится на серверном seed и БУКВАЛЬНО становится зачётной партией по
 *   «Начать» (preview = зачётная партия, I-1; «Начать» = `preStart→false` без
 *   второго раунд-трипа). «Перебросить старт» и «Играть ещё» — это тоже «открыть
 *   превью режима» (новый `POST /game/start` → новая сессия) — поэтому ОДНА точка
 *   правды на всех вызывающих (mode-select / board-rail / result), а не копия в
 *   каждом экране (CLAUDE.md «код через модули»; code-structure §1/§3).
 *
 * SCOPE: единственная операция «открыть превью режима» — `POST /game/start { mode }`
 *   (тело ТОЛЬКО `{ mode }`, daily-seed НЕ раскрывается — anti-shadow I-2 / AC-A.5)
 *   + store-переход в preStart-превью на серверном seed. На сетевой ошибке — toast,
 *   остаёмся там, где были (можно повторить).
 * NOT IN SCOPE: «Начать» (это чистый store `beginPreviewedGame`, без сети — здесь
 *   нет); resume открытой партии (lib/run-resume, RP-4); daily (своя поверхность).
 *
 * Cross-references:
 *   - run-persistence-lifecycle.md §4 (T-A) + addendum-001 Решение 1.
 *   - services/game-start.ts (POST /game/start wrapper — серверный seed/session).
 *   - store/game-store.ts (startAllTimeGame preStart=true → beginPreviewedGame).
 *   - screens/mode-select-screen.ts / screens/board-screen.ts / screens/result-screen.ts (callers).
 */

import type { GameMode } from '@lines98/shared/types/game';

import { startGame } from '../services/game-start.js';
import { useGameStore } from '../store/game-store.js';
import { showToast } from '../ui/toast.js';

import { t } from './i18n/index.js';

/**
 * Open the non-daily game screen in PRE-START PREVIEW of `mode` (T-A). Allocates a
 * fresh server session (`POST /game/start { mode }`, server seed) and mounts the
 * board in preview (`preStart=true`) on that seed — the LITERAL scored partition.
 * «Начать» later begins it via `store.beginPreviewedGame()` (no re-roll, AC-A.2).
 *
 * On a start failure (rate-limited / network) shows a toast and leaves the caller's
 * current screen unchanged (the player can retry). Returns `true` on success.
 */
export async function openModePreview(mode: GameMode): Promise<boolean> {
  const started = await startGame(mode);
  if (started.status === 'ok') {
    useGameStore.getState().startAllTimeGame({
      seed: started.seed,
      sessionId: started.sessionId,
      mode: started.mode,
      preStart: true,
    });
    return true;
  }
  showToast(
    started.status === 'rate_limited'
      ? t('game.modePreview.toastRateLimited')
      : t('game.modePreview.toastFail'),
  );
  return false;
}
